import { Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { StorageMap } from "@ngx-pwa/local-storage";
import { combineLatest, Observable } from "rxjs";
import { map, take } from "rxjs/operators";

import { currentRetailerStorageKey, localStorageKey, retailersStorageKey, userSessionStorageKey } from "@core/constants";
import { AppState } from "@core/store";
import * as actions from "../store/actions/login.action";

import { Router } from "@angular/router";
import { AuthService } from "@auth0/auth0-angular";
import { AdapterUi } from "@core/store/adapters/global.adapter";
import {
  AuthState,
  CargoAuthResponse,
  LOGIN_STAGE,
  LoginFormData,
  PasswordResetBody,
  RetailerInfo,
  TWOFACTOR_TYPE,
  UiAuth,
} from "@modules/auth/entities";
import * as AuthReducer from "@modules/auth/store/reducers/auth.reducer";

@Injectable({ providedIn: "root" })
export class AuthFacade {
  getUi$: Observable<UiAuth> = this.store.pipe(select(AuthReducer.getUiAuth));
  getAppUserId$: Observable<number> = this.store.pipe(select(AuthReducer.getAppUserId));
  loginStage$: Observable<LOGIN_STAGE> = this.getUi$.pipe(select(u => u.loginStage));
  getSessionInfo$ = this.storage.watch(localStorageKey).pipe(select(s => s));
  getTWOFAData$: Observable<AuthState["twofa"]> = this.store.pipe(select(AuthReducer.getTWOFAData));

  constructor(private store: Store<AppState>, private storage: StorageMap, private router: Router, private auth0Service: AuthService) {}

  get getCurrentView$(): Observable<string> {
    return this.getUi$.pipe(map(ui => ui.currentView));
  }

  get getDataAppUserId$(): Observable<number> {
    return this.getAppUserId$;
  }

  public doLoginAction(formData: LoginFormData) {
    this.store.dispatch(
      actions.basicLogin({
        formdata: { ...formData },
      })
    );
  }

  public continueWithoutTwoFactor(access_token: string) {
    this.store.dispatch(actions.fetchRetailerIds({ resp: { access_token } }));
  }

  public deleteConfig(configId: number) {
    this.store.dispatch(actions.deleteConfig({ configId }));
  }

  public twoFactorLogin(otpInput: string, type: TWOFACTOR_TYPE, id: number) {
    this.getTWOFAData$
      .pipe(
        take(1),
        map(data => {
          this.store.dispatch(
            actions.twoFactorLogin({
              access_token: data.preliminaryToken,
              two_factor_authentication_code: otpInput,
              twofactortype: type,
              fullHash: data.fullHash,
              id,
            })
          );
        })
      )
      .subscribe();
  }

  public doLogout() {
    this.store.dispatch(actions.wipeTwoFactorData());
    combineLatest([
      // this.auth0Service.logout(),
      this.storage.delete(localStorageKey),
      this.storage.delete(currentRetailerStorageKey),
      this.storage.delete(userSessionStorageKey),
      this.storage.delete(retailersStorageKey),
    ]).subscribe(() => this.router.navigate(["/auth"]));
  }

  public getAuth(): Observable<boolean> {
    return this.storage.watch(localStorageKey).pipe(
      map((authInfo: CargoAuthResponse) => {
        const token = authInfo?.access_token;
        return token && token?.length > 0;
      })
    );
  }

  public setTwoFactorData(data: Partial<AuthState["twofa"]>) {
    this.store.dispatch(actions.setTwoFactorData({ twofa: data }));
  }

  public async createRetailer(payload: RetailerInfo) {
    this.setUi([{ name: "loadBtn", value: true }]);
    return await this.store.dispatch(actions.createRetailer({ payload }));
  }

  public async passwordReset(payload: PasswordResetBody) {
    this.setUi([{ name: "loadBtn", value: true }]);
    return await this.store.dispatch(actions.passwordReset({ payload }));
  }

  public async requestPasswordReset(email: string) {
    this.setUi([{ name: "loadBtn", value: true }]);
    return await this.store.dispatch(actions.requestPasswordReset({ email }));
  }

  public setUi(setUi: Array<AdapterUi<AuthState["ui"]>>): void {
    this.store.dispatch(actions.uiAuth({ ui: setUi }));
  }

  public listTwoFactorConfigs() {
    this.store.dispatch(actions.listTwoFactorConfigs());
  }

  public setupTwoFactorMethod(method: TWOFACTOR_TYPE, phone?: string, nickname?: string) {
    this.getTWOFAData$
      .pipe(
        take(1),
        map(twofa => {
          this.store.dispatch(actions.setupTwoFactorAuthentication({ method, phone, accessToken: twofa.preliminaryToken, nickname }));
        })
      )
      .subscribe();
  }

  public turnOnTwoFactorMethod(method: TWOFACTOR_TYPE, otp: string, fullHash?: string) {
    this.getTWOFAData$
      .pipe(
        take(1),
        map(twofa => {
          this.store.dispatch(
            actions.turnOnTwoFactorAuthentication({
              method,
              otp,
              fullHash,
              accessToken: twofa.preliminaryToken,
              id: twofa.chosenMethod?.id,
            })
          );
        })
      )
      .subscribe();
  }

  public setupAdditionalTwoFactorMethod(method: TWOFACTOR_TYPE, phone?: string, nickname?: string) {
    this.store.dispatch(actions.setupAdditionalTwoFactorAuthentication({ method, phone, nickname }));
  }

  public turnOnAdditionalTwoFactorMethod(method: TWOFACTOR_TYPE, otp: string, fullHash?: string, id?: number) {
    this.store.dispatch(actions.turnOnAdditionalTwoFactorAuthentication({ method, otp, fullHash, authenticatorId: id }));
  }

  public requestPhoneOTP(id: number) {
    this.getTWOFAData$
      .pipe(
        take(1),
        map(twofa => {
          this.store.dispatch(actions.requestPhoneOTP({ id, accessToken: twofa.preliminaryToken }));
        })
      )
      .subscribe();
  }

  public fetch2FADeadline() {
    this.store.dispatch(actions.fetch2FADeadline());
  }
}
